import { Alert } from '@mui/material';
import { Typography } from '@mui/material';
import { Button, FlexContainer, FormBuilder } from '@act/storybook';
import { Controller, Control, FormState } from 'react-hook-form';
import { TrackingProvider } from 'react-components/tracking';
import {
  SearchBar,
  SelectOption,
} from 'react-components/components/SearchBar';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { AddCallerForm } from './useActiveCallAddParticipant.hook';

export interface ActiveCallMenuAddParticipantFormProps {
  isMemberKnown: boolean;
  isValid: boolean;
  guideId: string | null;
  control: Control<AddCallerForm>;
  formState: FormState<AddCallerForm>;
  addCallerFormConfig: FormConfig<AddCallerForm>;
  memberOptions: SelectOption[];
  isLoadingMembers: boolean;
  isLoadingTypeaheadMembers: boolean;
  selectedMemberOption: SelectOption | null;
  setSelectedMemberOption: (option: SelectOption | null) => void;
  setTypeaheadSearchTerm: (term: string) => void;
  isAddingGuideToCall: boolean;
  onSubmit: () => void;
}

/**
 * Internal-call add-participant form (guide + optional member).
 *
 * @param props Form wiring and handlers.
 * @returns Add participant panel.
 */
export const ActiveCallMenuAddParticipantForm = ({
  isMemberKnown,
  isValid,
  guideId,
  control,
  formState,
  addCallerFormConfig,
  memberOptions,
  isLoadingMembers,
  isLoadingTypeaheadMembers,
  selectedMemberOption,
  setSelectedMemberOption,
  setTypeaheadSearchTerm,
  isAddingGuideToCall,
  onSubmit,
}: ActiveCallMenuAddParticipantFormProps) => (
  <TrackingProvider contextName="add-connection-form">
    <FlexContainer
      direction="column"
      padding={2}
      backgroundColor="white"
      borderRadius="moreRounded"
      margin="0 0 24px 0"
      overflow="visible"
      overflowAllVisible
      height="auto"
    >
      <Typography
        variant="h6"
        color="text.primary"
        fontWeight={600}
        marginBottom={2}
      >
        Select guide to add to the call
      </Typography>

      {isValid && (
        <FlexContainer
          direction="column"
          margin="0 0 16px 0"
          overflow="visible"
          overflowAllVisible
          height="auto"
        >
          <Alert severity="info" variant="standard">
            Remember to notify the member that they will be on a brief hold and
            the line may go silent.
          </Alert>
        </FlexContainer>
      )}

      {!isMemberKnown && (
        <Controller
          control={control}
          name="memberId"
          render={({ field: { onChange, value } }) => {
            const selectedOption = value
              ? memberOptions.find(opt => opt.value === value) ||
                selectedMemberOption
              : null;

            return (
              <FlexContainer
                direction="column"
                margin="0 0 16px 0"
                overflow="visible"
                overflowAllVisible
                height="auto"
              >
                <SearchBar
                  label="Member's name (optional)"
                  options={memberOptions}
                  isLoading={isLoadingMembers || isLoadingTypeaheadMembers}
                  setTypeaheadSearchTerm={setTypeaheadSearchTerm}
                  setSearchTerm={(searchTerm: string | SelectOption) => {
                    if (
                      typeof searchTerm === 'object' &&
                      searchTerm?.value
                    ) {
                      setSelectedMemberOption(searchTerm);
                      onChange(searchTerm.value);
                    } else if (
                      typeof searchTerm === 'string' &&
                      searchTerm.trim() === ''
                    ) {
                      setSelectedMemberOption(null);
                      onChange(null);
                    }
                  }}
                  value={selectedOption?.label || ''}
                />
              </FlexContainer>
            );
          }}
        />
      )}

      <FormBuilder
        config={addCallerFormConfig}
        control={control}
        formState={formState}
      />

      <FlexContainer
        direction="row"
        justifyContent="flex-end"
        margin="16px 0 0 0"
        overflow="visible"
        overflowAllVisible
        height="auto"
      >
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={isAddingGuideToCall || !guideId || !isValid}
          trackingName="submit-add-caller-button"
        >
          Add caller
        </Button>
      </FlexContainer>
    </FlexContainer>
  </TrackingProvider>
);
